import React, { PropsWithChildren } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import PublishedScoreStatus from '@components/myScores/ScoresView/PublishedScoreStatus'
import AudioPreviewHandle from '@components/myScores/ScoresView/ScoreListView/AudioPreviewHandle'
import { ScoreViewOptions } from '@components/myScores/utils/types'
import NFIcon from '@components/shared/NFIcon'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Product from '@models/Product'
import Score from '@models/Score'
import { isProduct } from '@models/util/product.util'
import { isScore } from '@models/util/score.util'
import { useSelection } from '@providers/SelectionProvider'
import { formatDate } from '@utils/formatters/date.formatter'
import PageRoutes from '@utils/page-routes'

import CollectionTags from '../CollectionTags'
import ScoreTitle from '../ScoreTitle'
import ScoreTypeLabel from '../ScoreTypeLabel'
import IconHandle from './IconHandle'
import SocialSection from './SocialSection'

interface Props {
  score: Score | Product
  options?: ScoreViewOptions
  pendingDelete?: boolean
}

const ScoreRow: React.FC<Props> = ({ score, options, pendingDelete }) => {
  const { isSorting } = useSelection()
  const sortable = useSortable({ id: score.id, disabled: pendingDelete })
  const { t } = useTranslation()
  const navigate = useNavigate()

  const onClick = () => {
    let shouldNavigate = true
    if (typeof options?.beforeNavigate === 'function') {
      shouldNavigate = options.beforeNavigate(score)
    }

    if (shouldNavigate) {
      if (isProduct(score)) {
        if (score.music_url) {
          navigate(score.music_url)
        } else {
          window.location.href = score.marketplace_url
        }
      } else {
        window.location.href = PageRoutes.editorPath(score.external_id)
      }
    }
  }

  const renderIconHandle = () => {
    if (isSorting) {
      return (
        <NFIcon
          icon="handle"
          className="w-[25px] text-center mr-3.5 text-xl text-gray-icons dark:text-gray-icons-dm"
        />
      )
    }
    if (options?.readonly) return <AudioPreviewHandle score={score} />
    return <IconHandle score={score} />
  }

  return (
    <SortWrapper canSort={options?.sortable} sortable={sortable}>
      <button
        role="link"
        disabled={pendingDelete || isSorting}
        onClick={onClick}
        title={score.title ?? score.external_id}
        className={classNames(
          'w-full flex flex-row bg-white dark:bg-black-bg2 h-[40px] items-center px-2.5 rounded-sm mb-0.5 text-left min-w-0',
          {
            'opacity-50': pendingDelete,
            'opacity-75': sortable.isSorting && !sortable.isDragging,
            'cursor-grab': isSorting && !sortable.isDragging,
          },
          sortable.isDragging
            ? 'shadow-lg z-10 cursor-grabbing'
            : 'hover:bg-opacity-60 dark:hover:bg-opacity-60'
        )}>
        {/* Icon Handle */}
        {renderIconHandle()}

        <div className="basis-1/4 grow mr-2.5 flex flex-row min-w-0">
          <p className="ellipsify">
            {/* Title */}
            <ScoreTitle score={score} className="font-medium" />

            {/* Author */}
            {score.author?.username && options?.showAuthor && (
              <>
                <span className="text-gray-text dark:text-gray-text-dm mx-1 font-normal">
                  {t('content.authorPrefix')}
                </span>
                <a
                  href={PageRoutes.userProfilePath(score.author.external_id)}
                  title={score.author.username}
                  className="orange-link font-normal">
                  {score.author.username}
                </a>
              </>
            )}

            {/* Composer (score only) */}
            {isScore(score) && score.composer && (
              <span className="ml-4 text-gray-icons dark:text-gray-text-dm">{score.composer}</span>
            )}
          </p>

          {/* Official badge (product only) */}
          {isProduct(score) && score.official && (
            <NFIcon icon="original" className="ml-2 text-orange text-md" />
          )}
        </div>

        {/* Collections */}
        {!options?.hideCollections && (
          <CollectionTags
            collections={score.collections}
            numShown={2}
            className="basis-[content] mr-4 xl:mr-8 justify-end"
          />
        )}

        {/* Type (score only) */}
        {!options?.hideState && isScore(score) && (
          <ScoreTypeLabel score={score} className="basis-[100px] mr-2.5" />
        )}

        {/* Social */}
        {!options?.hideSocial && <SocialSection score={score} />}

        {/* Published Score (score only) */}
        {isScore(score) && (
          <PublishedScoreStatus score={score} className="basis-[10%] ellipsify mr-2.5" />
        )}

        {/* Updated */}
        {!options?.hideTimestamp && (
          <p className="text-sm text-gray-text dark:text-gray-text-dm grow-0 basis-[90px] ellipsify text-right">
            {formatDate(score.updated)}
          </p>
        )}
      </button>
    </SortWrapper>
  )
}

export default ScoreRow

interface SortProps {
  sortable: ReturnType<typeof useSortable>
  canSort?: boolean
}
const SortWrapper: React.FC<PropsWithChildren<SortProps>> = ({ children, sortable, canSort }) => {
  const { isSorting } = useSelection()

  if (isSorting && canSort) {
    return (
      <div
        ref={sortable.setNodeRef}
        style={{
          transform: CSS.Transform.toString(sortable.transform),
          transition: sortable.transition,
        }}
        {...sortable.listeners}
        {...sortable.attributes}>
        {children}
      </div>
    )
  }
  return <>{children}</>
}
